import { useParams, useSearch } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { pb } from '@/lib/pocketbase'
import { Collections } from '@/types/pocketbase-types'
import { useAuth } from '@/context/auth-context'
import Feedback from '@/components/application/feedback/Feedback'
import type { UserScoreResponse } from '@/types/pocketbase-types'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useSnackbar } from 'notistack'
import { useEffect, useRef } from 'react'

const ArticleQuizResultsPage = () => {
  const { slug: articleId } = useParams({ from: '/articles/quiz-results/$slug' })
  const { summaryLevel } = useSearch({ from: '/articles/quiz-results/$slug' })
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const hasShownToast = useRef(false)

  // Fetch user score for this article
  const { data: userScore, isLoading, error } = useQuery({
    queryKey: ['article-quiz-score', articleId, user?.id],
    queryFn: async (): Promise<UserScoreResponse | null> => {
      if (!user?.id) return null

      try {
        // Get the most recent quiz attempt by sorting by created date
        const record = await pb.collection(Collections.UserScore).getFirstListItem(
          `user="${user.id}" && article="${articleId}"`,
          {
            sort: '-created', // Sort by created date descending to get the latest
            requestKey: `article-quiz-${articleId}-${Date.now()}` // Force fresh fetch
          }
        ) as UserScoreResponse
        return record
      } catch (error: any) {
        if (error?.status === 404) return null
        throw error
      }
    },
    enabled: !!user?.id && !!articleId,
    staleTime: 0, // Always refetch
    gcTime: 0, // Don't cache
  })

  // Show points gained toast when score is loaded
  useEffect(() => {
    if (userScore && userScore.score !== undefined && !hasShownToast.current) {
      hasShownToast.current = true
      enqueueSnackbar(`🎉 You earned ${userScore.score} points!`, {
        variant: 'success',
        autoHideDuration: 4000,
      })
    }
  }, [userScore, enqueueSnackbar])

  if (isLoading) {
    return (
      <LoadingSpinner text="Loading your results..." className="min-h-screen" />
    )
  }

  if (error || !userScore) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-error-500 text-6xl mb-4">❌</div>
          <h2 className="text-xl font-bold text-error-500 mb-2">Error Loading Results</h2>
          <p className="text-neutral-500">
            {error ? 'Failed to load your quiz results.' : 'No quiz results found for this article.'}
          </p>
        </div>
      </div>
    )
  }

  // Calculate display values using new fields
  const displayScore = userScore.correct_answers || 0
  const totalQuestions = userScore.total_questions || 10
  const percentage = totalQuestions > 0 ? (displayScore / totalQuestions) * 100 : 0
  const stars = Math.round((percentage / 100) * 3)


  return (
    <Feedback
      score={displayScore}
      totalQuestions={totalQuestions}
      starsToFill={stars}
      slug={articleId}
      summaryLevel={summaryLevel}
    />
  )
}

export default ArticleQuizResultsPage